class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.setupCacheDetection();
  }

  setupCacheDetection() {
    window.addEventListener('pageshow', async event => {
      if (event.persisted) {
        await this.initialRenderCartDrawer();
      } else {
        const [entry] = performance.getEntriesByType('navigation');
        if (entry && entry.transferSize === 0 && entry.type === "back_forward") {
          await this.initialRenderCartDrawer();
        }
      }
    });
  }

  isCartItemsDifferent(currentCartItems, cartItems) {
    if (currentCartItems.length !== cartItems.length) return true;

    return currentCartItems.some((currentCartItem, index) => {
      const cartItem = cartItems[index];
      return currentCartItem.key !== cartItem.key || currentCartItem.quantity !== cartItem.quantity;
    });
  }

  async initialRenderCartDrawer() {
    try {
      const response = await fetch(`${routes.cart_url}.js`);
      if (!response.ok) {
        throw new Error(`Failed to fetch cart data: ${response.status} ${response.statusText}`);
      }
      const cartData = await response.json();

      const cartItemsScript = this.querySelector('.js-cart-items');
      const cartItems = cartItemsScript ? JSON.parse(cartItemsScript.textContent) : [];
      const isCartItemsDifferent = this.isCartItemsDifferent(cartData.items, cartItems);
      
      if (!isCartItemsDifferent) {
        return;
      }

      const sections = this.getSectionsToRender();
      const sectionIds = sections.map(section => section.id).join(',');
      
      const sectionsResponse = await fetch(`${window.location.pathname}?sections=${sectionIds}`);
      if (!sectionsResponse.ok) {
        throw new Error(`Failed to fetch sections data: ${sectionsResponse.status} ${sectionsResponse.statusText}`);
      }
      const sectionsData = await sectionsResponse.json();

      sections.forEach((section) => {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);

        if (!sectionElement) return;

        sectionElement.innerHTML = this.getSectionInnerHTML(sectionsData[section.id], section.selector);
      });

      this.classList.toggle('is-empty', cartData.item_count === 0);

      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: cartData });

      setTimeout(() => {
        this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      });
    } catch (e) {
      console.error(e);
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));

      // Force the browser to paint the freshly-inserted (closed) drawer before opening it,
      // otherwise the slide-in transition can skip straight to its end state with no animation.
      this.offsetHeight;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.open();
        });
      });
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
